/**
 * Client-side Geocodio helpers.
 * Geocode: Create Account address confirmation (lat/lng).
 * https://www.geocod.io/docs/#geocoding
 */

const GEOCODIO_SEARCH_URL = "https://api.geocod.io/v2/geocode";
const GEOCODIO_PARSE_URL = "https://api.geocod.io/v2/parse"; //  Parse: fleet location city/state (does not count as a geocode lookup).
const DEFAULT_DELAY_MS = 250;
const PARSE_CONCURRENCY = 20;
const MIN_ACCURACY = 0.8;
// https://www.geocod.io/docs/#accuracy-score
const STREET_LEVEL_ACCURACY_TYPES = new Set([
  "rooftop",
  "point",
  "range_interpolation",
  "nearest_rooftop_match",
]);
const cityStateCache = new Map();

/** Parse can split "Santa Monica" when the street is also named Santa Monica Pier. */
const CITY_STATE_OVERRIDES = new Map([
  ["200 santa monica pier, santa monica, ca 90401", "Santa Monica, CA"],
]);

function normalizeAddressKey(address) {
  return String(address ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

export const ADDRESS_NOT_FOUND_MESSAGE =
  "This address could not be found. Enter a valid street address and try again.";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function getGeocodioApiKey() {
  return String(process.env.REACT_APP_GEOCODIO_API_KEY ?? "").trim();
}

/**
 * @param {Response} response
 * @returns {Promise<string>}
 */
async function readGeocodioError(response) {
  try {
    const data = await response.json();
    if (typeof data?.error === "string" && data.error) {
      return data.error;
    }
  } catch {
    // Fall through to status-based message.
  }
  return `Location lookup failed (${response.status})`;
}

/**
 * @param {string} address
 * @returns {string}
 */
function streetNumberFromAddress(address) {
  const match = String(address)
    .trim()
    .match(/^(\d+[A-Za-z]?)/);
  return match ? match[1].toUpperCase() : "";
}

/**
 * @param {unknown} data
 * @param {string} address
 * @returns {{ latitude: number, longitude: number }}
 */
function parseGeocodioCoordinates(data, address) {
  const result = data?.results?.[0];
  const lat = Number(result?.location?.lat);
  const lon = Number(result?.location?.lng);
  const accuracy = Number(result?.accuracy);
  const accuracyType = String(result?.accuracy_type ?? "").toLowerCase();
  const returnedNumber = String(result?.address_components?.number ?? "").toUpperCase();
  const queriedNumber = streetNumberFromAddress(address);

  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    throw new Error(ADDRESS_NOT_FOUND_MESSAGE);
  }

  const isStreetLevel = STREET_LEVEL_ACCURACY_TYPES.has(accuracyType);
  const isAccurateEnough = Number.isFinite(accuracy) && accuracy >= MIN_ACCURACY;
  const isNearestRooftop = accuracyType === "nearest_rooftop_match";
  const houseNumberMatches =
    isNearestRooftop || !queriedNumber || !returnedNumber || returnedNumber === queriedNumber;

  if (!isStreetLevel || !isAccurateEnough || !houseNumberMatches) {
    throw new Error(ADDRESS_NOT_FOUND_MESSAGE);
  }

  return { latitude: lat, longitude: lon };
}

/**
 * @param {string} address
 * @returns {Promise<{ latitude: number, longitude: number }>}
 */
export async function geocodeAddress(address) {
  const trimmed = String(address ?? "").trim();
  if (!trimmed) {
    throw new Error("Address is empty");
  }

  const apiKey = getGeocodioApiKey();
  if (!apiKey) {
    throw new Error("Missing REACT_APP_GEOCODIO_API_KEY");
  }

  const url = new URL(GEOCODIO_SEARCH_URL);
  url.searchParams.set("q", trimmed);
  url.searchParams.set("api_key", apiKey);
  url.searchParams.set("limit", "1");
  url.searchParams.set("country", "USA");

  const response = await fetch(url.toString(), {
    headers: { Accept: "application/json" },
  });

  if (!response.ok) {
    const apiError = await readGeocodioError(response);
    if (response.status === 422) {
      throw new Error(ADDRESS_NOT_FOUND_MESSAGE);
    }
    throw new Error(apiError);
  }

  const data = await response.json();
  return parseGeocodioCoordinates(data, trimmed);
}

/**
 * Geocode one address per domicile. Dedupes identical addresses and spaces requests.
 *
 * @param {Record<string, string>} domicileAddressMap domicile → address
 * @param {{ delayMs?: number }} [options]
 * @returns {Promise<{
 *   results: Record<string, { address: string, latitude: number | null, longitude: number | null }>,
 *   errors: Record<string, string>
 * }>}
 */
export async function geocodeDomicileAddresses(domicileAddressMap, options = {}) {
  const delayMs = options.delayMs ?? DEFAULT_DELAY_MS;
  const results = {};
  const errors = {};
  const cache = new Map();

  const entries = Object.entries(domicileAddressMap ?? {});

  for (let i = 0; i < entries.length; i++) {
    const [domicile, rawAddress] = entries[i];
    const address = String(rawAddress ?? "").trim();

    if (!address) {
      results[domicile] = { address: "", latitude: null, longitude: null };
      continue;
    }

    if (cache.has(address)) {
      const cached = cache.get(address);
      if (cached.ok) {
        results[domicile] = { address, latitude: cached.latitude, longitude: cached.longitude };
      } else {
        errors[domicile] = cached.error;
      }
      continue;
    }

    try {
      const { latitude, longitude } = await geocodeAddress(address);
      cache.set(address, { ok: true, latitude, longitude });
      results[domicile] = { address, latitude, longitude };
    } catch (error) {
      const message = error?.message || "Location lookup failed";
      cache.set(address, { ok: false, error: message });
      errors[domicile] = message;
    }

    if (i < entries.length - 1 && delayMs > 0) {
      await sleep(delayMs);
    }
  }

  return { results, errors };
}

/**
 * @param {{ city?: unknown, state?: unknown, state_province?: unknown } | null | undefined} components
 * @returns {string} "City, ST" or ""
 */
export function formatCityState(components) {
  const city = String(components?.city ?? "").trim();
  const state = String(components?.state_province ?? components?.state ?? "").trim();
  if (city && state) return `${city}, ${state}`;
  return city || state;
}

/**
 * Parse one address into "City, ST". Empty string on failure (caller may fall back).
 * @param {string} address
 * @returns {Promise<string>}
 */
export async function parseAddressCityState(address) {
  const trimmed = String(address ?? "").trim();
  if (!trimmed) return "";
  if (cityStateCache.has(trimmed)) return cityStateCache.get(trimmed);

  const override = CITY_STATE_OVERRIDES.get(normalizeAddressKey(trimmed));
  if (override) {
    cityStateCache.set(trimmed, override);
    return override;
  }

  const apiKey = getGeocodioApiKey();
  if (!apiKey) return "";

  const url = new URL(GEOCODIO_PARSE_URL);
  url.searchParams.set("q", trimmed);
  url.searchParams.set("api_key", apiKey);

  const response = await fetch(url.toString(), {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    cityStateCache.set(trimmed, "");
    return "";
  }

  const data = await response.json();
  const label = formatCityState(data?.address_components);
  cityStateCache.set(trimmed, label);

  return label;
}

/**
 * Parse unique addresses in parallel. Returns address → "City, ST".
 * @param {string[]} addresses
 * @returns {Promise<Map<string, string>>}
 */
export async function parseCityStateForAddresses(addresses) {
  const unique = [
    ...new Set((addresses ?? []).map((value) => String(value ?? "").trim()).filter(Boolean)),
  ];
  const out = new Map();
  if (unique.length === 0 || !getGeocodioApiKey()) return out;

  const missing = unique.filter((address) => !cityStateCache.has(address));
  for (let i = 0; i < missing.length; i += PARSE_CONCURRENCY) {
    const chunk = missing.slice(i, i + PARSE_CONCURRENCY);
    await Promise.all(
      chunk.map(async (address) => {
        try {
          await parseAddressCityState(address);
        } catch {
          cityStateCache.set(address, "");
        }
      }),
    );
  }

  unique.forEach((address) => {
    out.set(address, cityStateCache.get(address) || "");
  });
  return out;
}
